import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import i18n from "i18n";
import { LocationService } from "services/LocationService";

import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import Loader from "components/MainApp/atoms/Loader";
import GenericErrorModal from "components/MainApp/atoms/Modals/GenericErrorModal";

import Panel from "components/MainApp/organisms/TogglePanel";
import LocationPanel from "components/MainApp/organisms/LocationPanel";
import Toggle from "components/MainApp/atoms/Forms/Toggle";
import DeleteModal from "../DeleteModal";
import styles from "./LocationDetail.module.scss";
import { handleErrors } from "helpers/utils/errors";
import { capitalize } from "helpers/utils/utils_general";

const typeMap = {
  ZONE: i18n.t("zone"),
  AREA: capitalize(i18n.t("sector")),
  SHELF: i18n.t("locations.type.SHELF").toLowerCase(),
  LEVEL: i18n.t("locations.type.LEVEL").toLowerCase(),
  PLACE: i18n.t("locations.type.PLACE").toLowerCase()
};

const articleMap = {
  ZONE: i18n.t("zone.article"),
  AREA: i18n.t("sector.article"),
  SHELF: i18n.t("shelf.article"),
  LEVEL: i18n.t("level.article"),
  PLACE: i18n.t("location.article")
};

const LocationDetailView = props => {
  const history = useHistory();
  const [deleteErrorModal, setdeleteErrorModal] = useState(false);
  const [deleteErrorMsg, setdeleteErrorMsg] = useState("");

  const [locationId, setlocationId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [location, setlocation] = useState(null);
  const [children, setchildren] = useState([]);
  const [node_code, setnode_code] = useState(null);
  const [deleteModalOpen, setdeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!props.match.params.key) return;
    if (!props.location.state.node_code) return;

    const id = props.match.params.key;
    setlocationId(id);
    const node_code = props.location.state.node_code;
    setnode_code(node_code);
  }, []);

  useEffect(() => {
    if (!locationId) return;
    setLoading(true);
    LocationService.location(locationId).then(location => {
      setLoading(false);
      setlocation(location);

      LocationService.locationsAll({
        node: node_code,
        parent: locationId
      }).then(locations => {
        const childrenAux = location.getLocationPanelsView(locations);
        setchildren(childrenAux);
      });
    });
  }, [locationId]);

  const headerOptions = [
    {
      icon: "icon-iconos_eliminar",
      name: "delete",
      handler: () => {
        setdeleteModalOpen(true);
      },
      tooltip: `Eliminar ${location && typeMap[location.type]}`
    },
    {
      icon: "icon-iconos_editar",
      name: "edit",
      handler: () => {
        history.push({
          pathname: `/admin/locations/edit/${locationId}`,
          state: {
            node_code: node_code,
            type: location.type,
            parent: location.parent ? location.parent.id : null
          }
        });
      },
      tooltip: capitalize(i18n.t("edit")) + ` ${location && typeMap[location.type]}`
    }
  ];

  const handleAddLocation = type => {
    history.push({
      pathname: `/admin/locations/add`,
      state: {
        node_code: node_code,
        type: type,
        parent: locationId
      }
    });
  };

  const goToLocation = (locationId, type) => {
    history.push({
      pathname: `/admin/locations/detail/${locationId}`,
      state: {
        node_code: node_code
      }
    });
    setlocationId(locationId);
  };

  const handleGoBack = () => {
    if (location.parent) {
      history.push({
        pathname: `/admin/locations/detail/${location.parent.id}`,
        state: {
          node_code: node_code
        }
      });
      setlocationId(location.parent.id);
    } else {
      history.push(`/admin/businessnodes/detail/${node_code}`);
    }
  };

  const handleChangeDispatchArea = () => {
    const data = { dispatch_area: !location.dispatch_area };

    setLoading(true);
    LocationService.updateLocation(location.id, data)
      .then(location => {
        setLoading(false);
        setlocation(location);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const closeDeleteModal = () => {
    setdeleteModalOpen(false);
  };

  const deleteLocation = () => {
    const parent = location.parent;
    setLoading(true);
    setdeleteModalOpen(false);

    LocationService.deleteLocation(locationId)
      .then(rta => {
        setLoading(false);
        if (parent) {
          history.push({
            pathname: `/admin/locations/detail/${parent.id}`,
            state: {
              node_code: node_code
            }
          });
          setlocationId(parent.id);
        } else {
          history.push(`/admin/businessnodes/detail/${node_code}`);
        }
      })
      .catch(async error => {
        setLoading(false);
        setdeleteErrorModal(true);
        const errorMsg = await handleErrors(error);
        setdeleteErrorMsg(errorMsg);
      });
  };

  const content = (
    <div className={styles["location-detail-content-wrapper"]}>
      {location && (
        <div>
          <div className={styles["edit-location-toggle"]}>
            <p>
              {i18n.t("generate.exit.order.automatically")}
            </p>
            <Toggle
              handleChange={handleChangeDispatchArea}
              checked={location.dispatch_area}
              name="dispatch_area"
            />
          </div>
          <Panel
            fields={location && location.getDescriptionPanelView()}
            title={capitalize(i18n.t("description"))}
          />
        </div>
      )}
      {children && (
        <LocationPanel
          key={children.id}
          title={children.title}
          type={children.subtitle}
          items={children?.locations || []}
          handleAdd={() => handleAddLocation(children.type)}
          handleClick={goToLocation}
        />
      )}
    </div>
  );
  return (
    <div className={styles["location-detail-container"]}>
      <AdminLayout
        headerTitle={
          location
            ? i18n.t("detail.of").concat(' ').concat(i18n.t(`${location && typeMap[location.type]}`))
            : ""
        }
        headerOptions={headerOptions}
        content={content}
        navHidden={true}
        goBackFunc={handleGoBack}
      ></AdminLayout>
      <DeleteModal
        open={deleteModalOpen}
        handleClose={closeDeleteModal}
        handleCancel={closeDeleteModal}
        handleConfirm={deleteLocation}
        locationName={location && location.name}
        locationType={location && articleMap[location.type]}
      />
      <GenericErrorModal
        open={deleteErrorModal}
        handleClose={() => setdeleteErrorModal(false)}
        error={deleteErrorMsg}
      ></GenericErrorModal>
      {loading && <Loader />}
    </div>
  );
};

export default LocationDetailView;
